import { AfterViewChecked, Component, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MiaV3Service } from '../services/mia-v3.service';
import { ImagemDoChat, MiaChatBackend } from './mia-chat-backend';
import { AudioGravado, GravadorDeAudioService, LIMITE_DE_GRAVACAO_SEGUNDOS,
         ProblemaDoMicrofone } from './gravador-de-audio.service';

interface ProdutoDaVitrine {
  id: number;
  nome: string;
  preco: number | null;
  precoMinimo?: number | null;
  descricao?: string;
  imagem: string;
}

interface MensagemSimulador {
  papel: 'cliente' | 'mia';
  texto: string;
  horario: Date;
  telemetria?: any;
  erroTecnico?: boolean;
  imagem?: string;
  linkCta?: string;
  // rótulo/rodapé do cta_url resolvidos no servidor, iguais ao envio real
  // (config da loja sobre os padrões "Fazer Pedido"/"Toque no botão...")
  ctaBotao?: string;
  ctaRodape?: string;
  // botões podem ter id lógico (ex.: ck_modo_entrega, do checkout conversacional)
  botoes?: Array<{ id?: string; titulo: string }>;
  lista?: { rotulo: string; itens: Array<{ titulo: string; descricao?: string; id?: string }> };
  listaAberta?: boolean;
  ferramentas?: Array<{ nome: string; argumentos: any; sucesso: boolean; duracaoMs: number }>;
  avisoPreco?: string;
  // vitrine de fotos: payload vindo na ação enviarLista; flowVitrine marca a
  // "mensagem de Flow" que o WhatsApp real enviaria após o toque na linha 📸
  vitrine?: { produtos: ProdutoDaVitrine[] };
  maisCategorias?: Array<{ nome: string }>;
  flowVitrine?: boolean;
  rotuloFlow?: string;
  flowCheckout?: boolean;
  // resumo do pedido (modo/endereço/taxa/total) que o Flow de pagamento exibe
  checkoutResumo?: any;
  // location_request_message: bolha da Mia com o botão nativo "Enviar localização"
  pedirLocalizacao?: boolean;
  pedidoCriado?: { codigo: string; total: number };
  // áudio gravado pelo cliente: o blob nunca sobe (a Mia ainda não transcreve),
  // então o player toca o arquivo local mesmo
  // `tocado` = índice da última barra reproduzida (-1 = nada). A posição fina
  // do marcador não mora aqui: quem a escreve é o rAF, direto no DOM.
  audio?: { url: string; urlSegura: any; duracaoSegundos: number; onda: number[]; transcricao?: string; tocado: number };
  // recado do simulador, não da conversa: silêncio da Mia, takeover, devolução
  notaSistema?: string;
  // emoji da ação "reagir" da Mia, pendurado na bolha do cliente como no WhatsApp
  reacao?: string;
}

const ID_ITEM_VITRINE = 'vitrine';
const ID_ITEM_MAIS_CATEGORIAS = 'mais_categorias';

const TEXTO_FALLBACK_PREFIXO = 'Desculpe, tive um probleminha técnico';

// genéricas: valem para qualquer cardápio; quando entram sugestões da própria
// loja, só as duas primeiras acompanham (o total fica em ~5 chips)
const SUGESTOES_GENERICAS = [
  'Monta um pedido pra 2 pessoas',
  'Taxa e prazo de entrega',
  'Me indica algo até R$ 50',
  'Quero montar meu pedido',
  'Estão abertos agora?'
];

/** Quanto da reprodução já passou, de 0 a 1 (0 = parado no começo). */
function fracaoTocada(segundosTocados: number, duracaoTotal: number): number {
  if (!duracaoTotal || segundosTocados <= 0) return 0;
  return Math.min(1, segundosTocados / duracaoTotal);
}

/**
 * Última fatia já reproduzida (-1 = nada). Serve à onda da bolha e ao scrubber
 * do pré-envio: parado no zero, nenhuma fatia acende.
 */
function fatiaAlcancada(fracao: number, fatias: number): number {
  return fracao > 0 ? Math.round(fracao * (fatias - 1)) : -1;
}

/** Saudação pelo relógio do visitante: 5h–11h59 dia, 12h–17h59 tarde, resto noite. */
function saudacaoDoMomento(): string {
  const hora = new Date().getHours();
  if (hora >= 5 && hora < 12) { return 'Bom dia'; }
  if (hora >= 12 && hora < 18) { return 'Boa tarde'; }
  return 'Boa noite';
}

@Component({
  selector: 'app-tela-teste-mia-v3',
  templateUrl: './tela-teste-mia-v3.component.html',
  styleUrls: ['./tela-teste-mia-v3.component.scss']
})
export class TelaTesteMiaV3Component implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('corpoChat') corpoChat: ElementRef;
  @ViewChild('campoMensagem') campoMensagem: ElementRef;
  @ViewChild('medidorGravacao') medidorGravacao: ElementRef;
  @ViewChild('medidorPainel') medidorPainel: ElementRef;

  // O MESMO componente atende a tela de teste do painel e o widget público de
  // demonstração (projeto loja). No modo widget somem as ferramentas internas
  // (toggles de teste, telemetria, chips de ferramenta, avisos de dev).
  @Input() modo: 'painel' | 'widget' = 'painel';
  // Backend injetado pelo host; sem ele, usa o MiaV3Service do painel.
  @Input() backend: MiaChatBackend | null = null;
  // Sugestões tiradas do cardápio da loja (vêm no /info do widget).
  @Input() sugestoesDaLoja: string[] | null = null;

  get ehPainel(): boolean {
    return this.modo !== 'widget';
  }

  private get servico(): MiaChatBackend {
    return this.backend || this.miaV3Service;
  }

  /** Erros técnicos: no painel apontam o serviço; no widget só pedem desculpa. */
  private textoErroTecnico(detalhePainel: string): string {
    return this.ehPainel ? detalhePainel : '⚠️ Tive um probleminha técnico aqui 🙏 Pode tentar de novo?';
  }

  mensagens: MensagemSimulador[] = [];
  textoDigitado = '';
  // imagem anexada aguardando envio (já redimensionada); o texto digitado vira a legenda
  imagemPendente: { base64: string; mimeType: string; preview: string } | null = null;
  erroImagem = '';
  digitando = false;
  avisoTecnico = false;
  telefoneSimulado = '';
  carrinho: any = null;
  carrinhoAberto = false;

  // saudação dinâmica na frente; recalculada a cada conversa nova
  sugestoes: string[] = [];

  private deveRolarParaOFim = false;

  // follow-up proativo: o worker entrega na transcrição; a sonda traz pro chat
  private sondaFollowups: any = null;
  private cursorFollowups = 0;
  followupAdiantando = false;

  constructor(private miaV3Service: MiaV3Service, public gravador: GravadorDeAudioService,
              private zone: NgZone) {}

  ngOnInit(): void {
    this.novaConversa();
    this.inicieSondaDeFollowups();
    // o medidor roda fora do zone: escreve direto no DOM, sem change detection
    this.gravador.aoMudarNivel = (nivel: number) => this.desenheNivel(nivel);
  }

  ngOnDestroy(): void {
    if (this.sondaFollowups) {
      clearInterval(this.sondaFollowups);
      this.sondaFollowups = null;
    }
    this.gravador.aoMudarNivel = null;
    this.pareDeSeguirAReproducao();
    this.pareOCronometro();
    this.gravador.cancele();
    this.gravador.fecheMonitor();
    this.gravador.descarte(this.audioPendente);
    this.gravador.descarte(this.audioDeTeste);
    this.libereAudiosDasBolhas();
  }

  /** Blobs das bolhas enviadas: sem isto, sair da tela os deixa presos na SPA. */
  private libereAudiosDasBolhas() {
    for (const mensagem of this.mensagens) {
      if (mensagem.audio?.url) URL.revokeObjectURL(mensagem.audio.url);
    }
  }

  ngAfterViewChecked(): void {
    if (this.deveRolarParaOFim && this.corpoChat) {
      this.corpoChat.nativeElement.scrollTop = this.corpoChat.nativeElement.scrollHeight;
      this.deveRolarParaOFim = false;
    }
  }

  novaConversa() {
    // telefone sintético único por sessão de teste (cabe no VARCHAR(20));
    // prefixo distingue a origem na tela de conversas (sim: painel · wdg: widget)
    this.telefoneSimulado = (this.ehPainel ? 'sim:' : 'wdg:') + Date.now().toString(36);
    const daLoja = (this.sugestoesDaLoja || []).slice(0, 2);
    this.sugestoes = [saudacaoDoMomento(), ...daLoja, ...SUGESTOES_GENERICAS.slice(0, daLoja.length ? 2 : undefined)];
    // os blobs das bolhas de áudio morrem junto com a conversa
    this.libereAudiosDasBolhas();
    this.mensagens = [];
    this.avisoTecnico = false;
    this.textoDigitado = '';
    this.imagemPendente = null;
    this.erroImagem = '';
    this.carrinho = null;
    this.carrinhoAberto = false;
    this.localizacaoEnviando = false;
    this.aguardandoTextoCheckout = false;
    // telefone novo = conversa nova: o takeover e a janela de aviso do áudio
    // ficaram com a sessão anterior
    this.emAtendimentoHumano = false;
    this.cursorFollowups = 0;
    this.followupAdiantando = false;
    this.canceleGravacao();
    this.descartePendente();
    setTimeout(() => this.campoMensagem?.nativeElement?.focus(), 0);
  }

  envieSugestao(sugestao: string) {
    this.textoDigitado = sugestao;
    this.envie();
  }

  /** Sonda leve (2,5s): só roda quando o backend expõe o método (painel). */
  private inicieSondaDeFollowups() {
    if (this.sondaFollowups || !this.servico.obtenhaFollowupsSimulador) return;
    this.sondaFollowups = setInterval(() => this.busqueFollowups(), 2500);
  }

  private busqueFollowups() {
    const backend = this.servico;
    if (!backend.obtenhaFollowupsSimulador || this.digitando) return;
    const telefone = this.telefoneSimulado;
    backend.obtenhaFollowupsSimulador(telefone, this.cursorFollowups).then((resposta: any) => {
      // a conversa pode ter sido trocada enquanto a consulta viajava
      if (telefone !== this.telefoneSimulado) return;
      for (const turno of resposta?.turnos || []) {
        this.cursorFollowups = Math.max(this.cursorFollowups, Number(turno.id) || 0);
        this.mensagens.push({ papel: 'mia', texto: String(turno.texto || ''), horario: new Date() });
        this.deveRolarParaOFim = true;
      }
    }).catch(() => {});
  }

  /** Antecipa o próximo lembrete pendente; o worker envia e a sonda traz pro chat. */
  adianteFollowup() {
    const backend = this.servico;
    if (!backend.adianteFollowupSimulador || this.followupAdiantando) return;
    this.followupAdiantando = true;
    backend.adianteFollowupSimulador(this.telefoneSimulado)
      .then((resposta: any) => {
        if (!resposta?.adiantado) {
          this.mensagens.push({
            papel: 'mia', texto: '',
            notaSistema: 'Nenhum follow-up pendente para adiantar — converse com a Mia e deixe a bola com o cliente.',
            horario: new Date(),
          });
          this.deveRolarParaOFim = true;
        }
      })
      .catch(() => {})
      .then(() => this.followupAdiantando = false);
  }

  // ==== "Salvar caso": a conversa vira um caso de eval (mia-v3/evals) ====
  salvandoCaso = false;

  /** Precisa de pelo menos uma mensagem do cliente para haver turno a testar. */
  get podeSalvarCaso(): boolean {
    return this.ehPainel && this.mensagens.some(mensagem => mensagem.papel === 'cliente');
  }

  /**
   * A última mensagem do cliente é o turno testado; tudo antes vira histórico
   * (botões e listas no formato da transcrição da Mia, "[A] [B]" e "• item");
   * o que a Mia respondeu depois vai como referência para escrever o esperado.
   * O Servidor junta a configuração dos turnos, o perfil/pedido fictícios e o
   * snapshot da loja, e o JSON é baixado — nada fica gravado.
   */
  salveCaso() {
    const backend = this.servico;
    if (!backend.salveCasoSimulador || this.salvandoCaso || !this.podeSalvarCaso) return;
    let ultimoCliente = -1;
    for (let i = this.mensagens.length - 1; i >= 0; i--) {
      if (this.mensagens[i].papel === 'cliente') { ultimoCliente = i; break; }
    }
    const historico = this.mensagens.slice(0, ultimoCliente)
      .map(mensagem => ({ papel: mensagem.papel, texto: this.textoParaCaso(mensagem) }))
      .filter(mensagem => mensagem.texto);
    const turno = this.textoParaCaso(this.mensagens[ultimoCliente]);
    const respostaObservada = this.mensagens.slice(ultimoCliente + 1)
      .map(mensagem => this.textoParaCaso(mensagem)).filter(Boolean).join('\n\n') || undefined;

    this.salvandoCaso = true;
    backend.salveCasoSimulador({
      telefone: this.telefoneSimulado, historico, turno, respostaObservada,
      simularPedidoAberto: this.pedidoAbertoTeste, perfilSimulado: this.perfilSimuladoParaEnvio,
    })
      .then((caso: any) => {
        this.baixeJson(`${caso?.id || 'caso'}.json`, caso);
        this.mensagens.push({
          papel: 'mia', texto: '',
          notaSistema: `Caso "${caso?.id}" baixado. Coloque em mia-v3/evals/casos/dev/ e edite o "esperado" antes de rodar.`,
          horario: new Date(),
        });
        this.deveRolarParaOFim = true;
      })
      .catch((erro: any) => {
        this.mensagens.push({
          papel: 'mia', texto: '',
          notaSistema: erro?.error?.erro || 'Não consegui montar o caso — verifique se a Mia V3 está rodando.',
          horario: new Date(),
        });
        this.deveRolarParaOFim = true;
      })
      .then(() => this.salvandoCaso = false);
  }

  /** Mesma renderização da transcrição da Mia (renderizeParaTranscricao), para o histórico do caso bater com o real. */
  private textoParaCaso(mensagem: MensagemSimulador): string {
    if (mensagem.erroTecnico || mensagem.notaSistema) return '';
    if (mensagem.papel === 'cliente') {
      if (mensagem.audio) return mensagem.audio.transcricao || '[🎤 Áudio enviado pelo cliente]';
      if (mensagem.imagem) return `[📷 Imagem enviada pelo cliente]${mensagem.texto ? ` "${mensagem.texto}"` : ''}`;
      return (mensagem.texto || '').trim();
    }
    const partes: string[] = [];
    if (mensagem.imagem) partes.push(`📷 ${mensagem.texto || '(foto)'}`.trim());
    else if (mensagem.texto) partes.push(mensagem.texto);
    if (mensagem.botoes?.length) partes.push(mensagem.botoes.map(botao => `[${botao.titulo}]`).join(' '));
    if (mensagem.lista?.itens?.length) {
      partes.push(mensagem.lista.itens.map(item => `• ${item.titulo}${item.descricao ? ` — ${item.descricao}` : ''}`).join('\n'));
    }
    if (mensagem.linkCta) partes.push(`[🔗 ${mensagem.linkCta}]`);
    return partes.join('\n').trim();
  }

  private baixeJson(nome: string, dados: any) {
    const blob = new Blob([JSON.stringify(dados, null, 2) + '\n'], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // ==== bottom sheet da lista (como o WhatsApp oficial) ====
  listaAberta: MensagemSimulador | null = null;
  opcaoSelecionada: string | null = null;

  abraLista(mensagem: MensagemSimulador) {
    this.listaAberta = mensagem;
    this.opcaoSelecionada = null;
  }

  fecheLista() {
    this.listaAberta = null;
    this.opcaoSelecionada = null;
  }

  confirmeLista() {
    if (!this.opcaoSelecionada || this.digitando) return;
    const escolha = this.opcaoSelecionada;
    const mensagem = this.listaAberta;

    const itemEscolhido = mensagem?.lista?.itens.find(item => item.titulo === escolha);

    // itens do checkout conversacional (endereços/zonas): clique vai à máquina de estados
    if (itemEscolhido?.id && itemEscolhido.id.startsWith('ck_')) {
      this.fecheLista();
      this.mensagens.push({ papel: 'cliente', texto: escolha, horario: new Date() });
      this.deveRolarParaOFim = true;
      this.envieEventoCheckout({ tipo: 'clique', id: itemEscolhido.id, rotulo: escolha });
      return;
    }

    // linha "➕ Ver mais": próxima página de categorias, montada localmente
    // (como o Despachante faz no WhatsApp real — sem LLM)
    if (itemEscolhido?.id === ID_ITEM_MAIS_CATEGORIAS && mensagem?.maisCategorias?.length) {
      this.fecheLista();
      this.mensagens.push({ papel: 'cliente', texto: escolha, horario: new Date() });

      const visiveis = mensagem.maisCategorias.slice(0, 9);
      const restantes = mensagem.maisCategorias.slice(9);
      const itens: Array<{ titulo: string; id?: string }> = visiveis.map(categoria => ({ titulo: categoria.nome }));
      if (restantes.length > 0) itens.push({ id: ID_ITEM_MAIS_CATEGORIAS, titulo: '➕ Ver mais' });

      this.mensagens.push({
        papel: 'mia',
        texto: 'Aqui estão as outras categorias 😊',
        lista: { rotulo: 'Ver categorias', itens },
        maisCategorias: restantes.length > 0 ? restantes : undefined,
        horario: new Date()
      });
      this.deveRolarParaOFim = true;
      return;
    }

    // linha "📸 Ver com fotos": no WhatsApp real chega uma mensagem de Flow;
    // aqui simulamos essa mensagem com o botão que abre a vitrine
    if (itemEscolhido?.id === ID_ITEM_VITRINE && mensagem?.vitrine) {
      this.fecheLista();
      this.mensagens.push({
        papel: 'cliente', texto: escolha, horario: new Date()
      });
      this.mensagens.push({
        papel: 'mia',
        texto: 'Preparei uma vitrine com as fotos pra você escolher 😊',
        flowVitrine: true,
        vitrine: mensagem.vitrine,
        horario: new Date()
      });
      this.deveRolarParaOFim = true;
      return;
    }

    this.fecheLista();
    this.envieSugestao(escolha);
  }

  // ==== vitrine de fotos (simula o WhatsApp Flow) ====
  vitrineAberta: MensagemSimulador | null = null;
  produtoVitrineSelecionado: ProdutoDaVitrine | null = null;

  abraVitrine(mensagem: MensagemSimulador) {
    this.vitrineAberta = mensagem;
    this.produtoVitrineSelecionado = null;
  }

  fecheVitrine() {
    this.vitrineAberta = null;
    this.produtoVitrineSelecionado = null;
  }

  confirmeVitrine() {
    if (!this.produtoVitrineSelecionado || this.digitando) return;
    const produto = this.produtoVitrineSelecionado;
    this.fecheVitrine();
    this.envieSugestao(`Quero o ${produto.nome}`);
  }

  // ==== checkout conversacional (botões/lista na conversa; Flow só no pagamento) ====
  // A máquina de estados roda no Servidor (a MESMA do WhatsApp real); aqui só
  // renderizamos as ações como bolhas e devolvemos cliques/textos/pin.
  checkoutAberto = false;
  checkoutOpcoes: any = null;
  checkoutPasso = 1; // 1 = pagamento · 2 = troco (só Dinheiro)
  checkoutPagamento: any = null;
  checkoutPrecisaTroco = false;
  checkoutTroco: number | null = null;
  checkoutEnviando = false;
  checkoutErro = '';
  checkoutResumo: any = null; // modo/endereço/taxa/total vindos da máquina

  // o próximo texto digitado é resposta de um passo do checkout (rua/número/bairro)
  aguardandoTextoCheckout = false;

  // ferramenta de teste: ignora o endereço salvo do contato p/ exercitar o
  // fluxo de localização (cenário real de cliente sem cadastro). Não muda nada
  // no servidor — só faz a máquina fingir que o contato não tem endereço salvo.
  clienteNovoTeste = false;

  // ferramenta de teste: a Mia recebe um pedido em aberto fictício (#4521, em
  // preparação) como se o cliente tivesse acabado de pedir — nada é criado.
  pedidoAbertoTeste = false;

  // ferramenta de teste: perfil fictício de cliente conhecido. Vale no primeiro
  // turno (saudação) e no fechamento (endereço de sempre, modo e pagamento
  // habituais); trocar reinicia a conversa, como aconteceria com outro cliente.
  perfilTeste = 'NOVO';
  readonly perfisDeTeste = [
    { id: 'NOVO', rotulo: 'Cliente novo', descricao: 'Sem histórico: a Mia se apresenta e o fechamento não sugere nada.' },
    { id: 'HABITUAL_ENTREGA_PIX', rotulo: 'Habitual · entrega · Pix', descricao: 'Pede pela conversa, recebe no endereço salvo do contato de teste e paga com Pix.' },
    { id: 'HABITUAL_RETIRADA_DINHEIRO', rotulo: 'Habitual · retirada · dinheiro', descricao: 'Pede pela conversa, retira na loja e paga em dinheiro (o troco é perguntado).' },
    { id: 'PEDE_PELO_LINK', rotulo: 'Pede pelo link', descricao: 'Nunca pediu pela conversa: a Mia oferece o link como caminho principal.' },
  ];

  get descricaoDoPerfilTeste(): string {
    return this.perfisDeTeste.find(perfil => perfil.id === this.perfilTeste)?.descricao || '';
  }

  /** Só o painel manda o perfil; o widget nunca simula (undefined = perfil real do contato). */
  private get perfilSimuladoParaEnvio(): string | undefined {
    return this.ehPainel ? this.perfilTeste : undefined;
  }

  troquePerfilTeste(perfil: string) {
    if (perfil === this.perfilTeste) return;
    this.perfilTeste = perfil;
    // o perfil só entra no primeiro turno: conversa em andamento não muda de cliente
    if (this.mensagens.length) this.novaConversa();
  }

  /** A Mia chamou iniciar_checkout: a máquina assume a conversa (botões, $0 de IA). */
  private inicieCheckoutConversacional() {
    this.envieEventoCheckout({ tipo: 'iniciar', ignorarEnderecosSalvos: this.clienteNovoTeste,
      perfilSimulado: this.perfilSimuladoParaEnvio });
  }

  /** Envia um evento à máquina de estados e renderiza as ações como bolhas. */
  private envieEventoCheckout(evento: any, aoFalharNoLLM?: string) {
    this.digitando = true;
    this.servico.envieEventoCheckout(this.telefoneSimulado, evento)
      .then((resposta: any) => {
        this.digitando = false;
        if (!resposta?.ativo && aoFalharNoLLM) {
          // a máquina devolveu o texto ao LLM (ex.: resposta não parseável)
          this.aguardandoTextoCheckout = !!resposta?.aguardandoTexto;
          this.digitando = true;
          this.envieTurnoLLM(aoFalharNoLLM);
          return;
        }
        this.renderizeAcoesCheckout(resposta);
        setTimeout(() => this.campoMensagem?.nativeElement?.focus(), 0);
      })
      .catch(() => {
        this.digitando = false;
        this.avisoTecnico = true;
        this.mensagens.push({
          papel: 'mia',
          texto: this.textoErroTecnico('⚠️ Não consegui processar o fechamento — verifique o Servidor.'),
          erroTecnico: true,
          horario: new Date()
        });
        this.deveRolarParaOFim = true;
      });
  }

  private renderizeAcoesCheckout(resposta: any) {
    this.aguardandoTextoCheckout = !!resposta?.aguardandoTexto;
    for (const acao of resposta?.acoes || []) {
      if (acao.tipo === 'enviarTexto' && acao.texto) {
        this.mensagens.push({ papel: 'mia', texto: acao.texto, horario: new Date() });
      } else if (acao.tipo === 'enviarBotoes' && acao.botoes?.length) {
        this.mensagens.push({ papel: 'mia', texto: acao.texto || '', botoes: acao.botoes, horario: new Date() });
      } else if (acao.tipo === 'enviarLista' && acao.itens?.length) {
        this.mensagens.push({
          papel: 'mia',
          texto: acao.texto || '',
          lista: { rotulo: acao.rotuloBotao || 'Escolher', itens: acao.itens },
          horario: new Date()
        });
      } else if (acao.tipo === 'pedirLocalizacao' && acao.texto) {
        this.mensagens.push({ papel: 'mia', texto: acao.texto, pedirLocalizacao: true, horario: new Date() });
      } else if (acao.tipo === 'abrirFlowCheckout') {
        this.mensagens.push({
          papel: 'mia',
          texto: acao.texto || 'Toque abaixo para finalizar o pagamento:',
          flowCheckout: true,
          checkoutResumo: acao.resumo || null,
          horario: new Date()
        });
      }
    }
    this.deveRolarParaOFim = true;
  }

  /** Clique num reply button: checkout (id ck_*) vai à máquina; o resto vira texto pra Mia. */
  // ==== volta da página de montagem (modo Mia) ====

  /** Tokens dos links /montar/ que esta conversa recebeu: só a página deles pode devolver bolha. */
  private tokensDeMontagem = new Set<string>();

  /**
   * A página de montagem, aberta pelo botão de link, avisa por postMessage
   * quando o cliente conclui e se fecha: a bolha que a Mia manda no WhatsApp
   * real entra aqui na hora, e os botões seguem o fluxo normal do simulador.
   */
  @HostListener('window:message', ['$event'])
  aoReceberMensagemDaPagina(evento: MessageEvent) {
    const dados: any = evento?.data;
    if (!dados || dados.tipo !== 'mia-montagem-concluida') return;
    if (!this.tokensDeMontagem.has(String(dados.token || ''))) return;
    this.tokensDeMontagem.delete(String(dados.token));
    // o item entrou no carrinho da conversa pela página: a barra reflete na hora
    if (dados.carrinho?.itens) this.carrinho = dados.carrinho;
    const aviso = dados.aviso;
    if (!aviso?.texto) return;
    this.mensagens.push({
      papel: 'mia',
      texto: String(aviso.texto),
      botoes: (Array.isArray(aviso.botoes) ? aviso.botoes : []).map((titulo: any) => ({ titulo: String(titulo) })),
      horario: new Date(),
    });
    this.deveRolarParaOFim = true;
  }

  cliqueBotao(botao: { id?: string; titulo: string }) {
    if (this.digitando) return;
    if (botao.id && botao.id.startsWith('ck_')) {
      this.mensagens.push({ papel: 'cliente', texto: botao.titulo, horario: new Date() });
      this.deveRolarParaOFim = true;
      this.envieEventoCheckout({ tipo: 'clique', id: botao.id, rotulo: botao.titulo });
      return;
    }
    this.escolhaOpcao(botao.titulo);
  }

  // ==== wizard de pagamento (o Flow curto do final: pagamento → troco → confirmar) ====

  abraCheckout(mensagem?: MensagemSimulador) {
    this.checkoutAberto = true;
    this.checkoutErro = '';
    this.checkoutPasso = 1;
    this.checkoutPagamento = null;
    this.checkoutPrecisaTroco = false;
    this.checkoutTroco = null;
    this.checkoutResumo = mensagem?.checkoutResumo || this.checkoutResumo;
    if (!this.checkoutOpcoes) {
      this.servico.obtenhaOpcoesCheckout()
        .then(opcoes => this.checkoutOpcoes = opcoes)
        .catch(() => this.checkoutErro = 'Não consegui carregar as formas de pagamento.');
    }
  }

  fecheCheckout() {
    this.checkoutAberto = false;
  }

  get checkoutUltimoPasso(): number {
    return this.checkoutPagamento?.pedirTroco ? 2 : 1;
  }

  get checkoutTituloDoPasso(): string {
    return this.checkoutPasso === 1 ? 'Como vai pagar?' : 'Precisa de troco?';
  }

  get checkoutRotuloAvancar(): string {
    if (this.checkoutEnviando) return 'Criando pedido…';
    return this.checkoutPasso < this.checkoutUltimoPasso ? 'Continuar' : 'Confirmar pedido';
  }

  get checkoutPodeAvancar(): boolean {
    if (this.checkoutEnviando) return false;
    if (this.checkoutPasso === 1) return !!this.checkoutPagamento;
    return !this.checkoutPrecisaTroco || Number(this.checkoutTroco) > 0;
  }

  get checkoutResumoTexto(): string {
    const resumo = this.checkoutResumo;
    if (!resumo) return '';
    const formate = (valor: number) => 'R$ ' + Number(valor).toFixed(2).replace('.', ',');
    const partes: string[] = [];
    if (resumo.modo === 'DELIVERY') {
      partes.push(`🏠 Entrega${resumo.enderecoResumo ? ' — ' + resumo.enderecoResumo : ''}`);
      partes.push(resumo.taxaGratis ? 'Entrega grátis 🎉' : `Taxa ${formate(resumo.taxa || 0)}`);
    } else {
      partes.push('🏪 Retirada na loja');
    }
    partes.push(`Total ${formate(resumo.total || 0)}`);
    return partes.join(' · ');
  }

  avanceCheckout() {
    if (!this.checkoutPodeAvancar) return;
    if (this.checkoutPasso === 1 && this.checkoutPagamento?.pedirTroco) {
      this.checkoutPasso = 2;
      return;
    }
    this.confirmeCheckout();
  }

  volteCheckout() {
    if (this.checkoutPasso === 2) this.checkoutPasso = 1;
  }

  // ==== localização (pin na CONVERSA, como no WhatsApp real — Flows não têm componente de localização) ====
  localizacaoEnviando = false;

  /**
   * Simula o cliente compartilhando o pin (clipe do WhatsApp ou o botão da
   * location_request_message). Se o checkout está ativo, o pin é o endereço do
   * passo atual (máquina de estados); fora do checkout, a Mia confirma o
   * endereço e ele fica guardado p/ o próximo fechamento.
   */
  envieLocalizacaoNaConversa() {
    if (this.localizacaoEnviando || this.digitando) return;
    this.localizacaoEnviando = true;
    this.mensagens.push({ papel: 'cliente', texto: '📍 Localização compartilhada', horario: new Date() });
    this.deveRolarParaOFim = true;

    this.servico.envieEventoCheckout(this.telefoneSimulado, { tipo: 'localizacao' })
      .then((resposta: any) => {
        if (resposta?.ativo) {
          this.localizacaoEnviando = false;
          this.renderizeAcoesCheckout(resposta);
          return null;
        }
        // sem checkout ativo: pin avulso (confirma endereço e registra p/ depois)
        return this.servico.envieLocalizacaoCheckout(this.telefoneSimulado)
          .then((avulso: any) => {
            this.localizacaoEnviando = false;
            this.mensagens.push({ papel: 'mia', texto: this.textoDoPinAvulso(avulso), horario: new Date() });
            this.deveRolarParaOFim = true;
          });
      })
      .catch(() => {
        this.localizacaoEnviando = false;
        this.mensagens.push({
          papel: 'mia',
          texto: this.textoErroTecnico('⚠️ Não consegui processar a localização — verifique o Servidor.'),
          erroTecnico: true,
          horario: new Date()
        });
        this.deveRolarParaOFim = true;
      });
  }

  /** Respostas idênticas às que o Despachante envia no WhatsApp real. */
  private textoDoPinAvulso(resposta: any): string {
    if (!resposta?.encontrado) {
      return 'Recebi sua localização, mas não consegui identificar o endereço por ela 🙏 Me diga sua rua ou o endereço por escrito?';
    }
    const lugar = [resposta.endereco?.logradouro, resposta.endereco?.bairro].filter(Boolean).join(', ');
    const taxa = resposta.taxa;
    if (taxa && !taxa.fazEntrega) {
      return `📍 Achei: *${lugar}*. Poxa, esse endereço ainda está fora da nossa área de entrega 🙏 Mas você pode retirar seu pedido na loja!`;
    }
    const textoTaxa = taxa
      ? (taxa.gratis ? ' A entrega aí é *grátis*! 🎉' : ` A entrega aí fica *R$ ${Number(taxa.valor).toFixed(2).replace('.', ',')}* 🏍️`)
      : '';
    return `📍 Achei: *${lugar}*.${textoTaxa} Vou usar esse endereço no fechamento 😉`;
  }

  private confirmeCheckout() {
    if (!this.checkoutPagamento || this.checkoutEnviando) return;
    this.checkoutEnviando = true;
    this.checkoutErro = '';

    this.servico.confirmeCheckout({
      telefone: this.telefoneSimulado,
      formaDePagamentoId: this.checkoutPagamento.id,
      trocoPara: (this.checkoutPrecisaTroco && this.checkoutTroco) ? this.checkoutTroco : undefined,
    }).then((resultado: any) => {
      this.checkoutEnviando = false;
      if (resultado?.erro) {
        this.checkoutErro = resultado.erro.mensagem || 'Não foi possível fechar o pedido.';
        return;
      }
      this.fecheCheckout();
      this.carrinho = null;
      this.carrinhoAberto = false;
      const resumo = this.checkoutResumo;
      const total = `R$ ${Number(resultado.pedido.total).toFixed(2).replace('.', ',')}`;
      const entregaTxt = resumo?.modo === 'DELIVERY'
        ? (resumo?.taxaGratis ? ' (entrega grátis 🚚)' : resumo?.taxa ? ` (com entrega de R$ ${Number(resumo.taxa).toFixed(2).replace('.', ',')} 🚚)` : '')
        : ' — pagamento na retirada';
      this.mensagens.push({
        papel: 'mia',
        texto: `🎉 Pedido *#${resultado.pedido.codigo}* confirmado! Total: ${total}${entregaTxt}. Já está com a loja! 😊`,
        pedidoCriado: resultado.pedido,
        horario: new Date()
      });
      this.checkoutResumo = null;
      this.deveRolarParaOFim = true;
    }).catch(() => {
      this.checkoutEnviando = false;
      this.checkoutErro = 'Erro ao fechar o pedido — verifique o Servidor.';
    });
  }

  precoDaVitrine(produto: ProdutoDaVitrine): string {
    const formate = (valor: number) => 'R$ ' + valor.toFixed(2).replace('.', ',');
    const preco = produto.preco ?? 0;
    const minimo = produto.precoMinimo ?? 0;
    if (minimo > 0 && minimo > preco) return 'a partir de ' + formate(minimo);
    if (preco > 0) return formate(preco);
    if (minimo > 0) return 'a partir de ' + formate(minimo);
    return '';
  }

  escolhaOpcao(titulo: string) {
    if (this.digitando) return;
    this.envieSugestao(titulo);
  }

  // ==== imagem anexada (como o clipe do WhatsApp) ====

  aoEscolherImagem(evento: any) {
    const arquivo: File | undefined = evento?.target?.files?.[0];
    evento.target.value = ''; // permite escolher o mesmo arquivo de novo
    if (!arquivo || this.digitando) return;
    this.erroImagem = '';
    this.redimensioneImagem(arquivo)
      .then(imagem => {
        this.imagemPendente = imagem;
        setTimeout(() => this.campoMensagem?.nativeElement?.focus(), 0);
      })
      .catch(() => {
        this.erroImagem = 'Não consegui ler essa imagem — tente outro arquivo.';
      });
  }

  removaImagemPendente() {
    this.imagemPendente = null;
  }

  /**
   * Redimensiona no navegador (máx. 1600px, JPEG) antes do upload — mesma ideia
   * da compressão do WhatsApp; mantém o payload bem abaixo do limite do Servidor.
   */
  private redimensioneImagem(arquivo: File): Promise<{ base64: string; mimeType: string; preview: string }> {
    return new Promise((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onerror = () => reject(new Error('falha ao ler o arquivo'));
      leitor.onload = () => {
        const elemento = new Image();
        elemento.onerror = () => reject(new Error('arquivo não é uma imagem legível'));
        elemento.onload = () => {
          try {
            const LADO_MAXIMO = 1600;
            const escala = Math.min(1, LADO_MAXIMO / Math.max(elemento.width, elemento.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(elemento.width * escala));
            canvas.height = Math.max(1, Math.round(elemento.height * escala));
            const contexto = canvas.getContext('2d');
            contexto.fillStyle = '#fff'; // PNG transparente ganha fundo branco no JPEG
            contexto.fillRect(0, 0, canvas.width, canvas.height);
            contexto.drawImage(elemento, 0, 0, canvas.width, canvas.height);
            const jpeg = canvas.toDataURL('image/jpeg', 0.85);
            resolve({ base64: jpeg.split(',')[1] || '', mimeType: 'image/jpeg', preview: jpeg });
          } catch (erro) {
            reject(erro);
          }
        };
        elemento.src = String(leitor.result);
      };
      leitor.readAsDataURL(arquivo);
    });
  }

  // ==== áudio do cliente (microfone, como o botão de gravar do WhatsApp) ====
  //
  // Quem responde é o comportamento_audio da loja (entender e responder / pedir
  // texto / chamar atendente / não responder). O binário não sobe — o servidor
  // só precisa saber que chegou um áudio, e o player da bolha toca o blob local
  // mesmo. Sem binário não há o que transcrever: com "entender e responder" o
  // servidor devolve uma nota explicando que a transcrição só vale no WhatsApp.

  microfoneAberto = false;
  gravando = false;
  segundosGravados = 0;
  audioPendente: AudioGravado | null = null;   // gravado, aguardando o envio
  audioDeTeste: AudioGravado | null = null;    // "gravar e ouvir" do painel
  gravandoTeste = false;
  // takeover: a Mia saiu da conversa até alguém devolver
  emAtendimentoHumano = false;
  devolvendoParaMia = false;

  private cronometro: any = null;
  private historicoDeNivel: number[] = new Array(48).fill(0);
  private frameDaReproducao = 0;
  /** Player que instalou o rAF vigente — só ele pode cancelá-lo. */
  private donoDaReproducao: HTMLAudioElement | null = null;

  /**
   * Alturas em % da onda da bolha, tiradas dos picos REAIS do arquivo gravado.
   * Piso de 14% só para o traço fino do silêncio não sumir. Se o navegador não
   * decodificou o formato, devolve vazio e a bolha cai num traço uniforme —
   * melhor que desenhar uma onda bonita que não corresponde ao áudio.
   */
  private alturasDaOnda(picos: number[]): number[] {
    return (picos || []).map(pico => Math.max(14, Math.round(pico * 100)));
  }
  // pontinhos da barra de áudio: o *ngFor só cria os elementos, quem anima é o
  // desenheNivel (fora do zone). No pré-envio viram o scrubber da reprodução.
  barrasDoMedidor = new Array(34).fill(0);
  /** Até onde a reprodução chegou no pré-envio (-1 = nada tocado ainda). */
  pontoTocado = -1;

  get problemaDoMicrofone(): ProblemaDoMicrofone | null {
    return this.gravador.problema;
  }

  /** Botão do microfone: grava direto, ou abre o painel quando algo está errado. */
  cliqueMicrofone() {
    if (this.digitando || this.emAtendimentoHumano) return;
    if (this.gravando) {
      this.pareGravacao();
      return;
    }

    // dica ("ainda não ouvi nada") não impede gravar; falha de verdade sim
    if (this.gravador.problema?.bloqueia) {
      this.abraMicrofone();
      return;
    }

    // primeira vez (ou permissão ainda não concedida): confere antes de gravar
    this.gravador.permissaoJaConcedida().then(concedida => {
      if (concedida) this.comeceGravacao();
      else this.abraMicrofone();
    });
  }

  comeceGravacao() {
    if (this.gravando) return;
    this.gravador.descarte(this.audioPendente);
    this.audioPendente = null;

    this.gravador.comece().then(comecou => {
      if (!comecou) {
        this.abraMicrofone(); // o painel explica o que houve e oferece a saída
        return;
      }
      this.gravando = true;
      this.segundosGravados = 0;
      this.contaOTempoDeGravacao();
    });
  }

  /** Cronômetro da barra; o teto encerra a gravação sozinho. */
  private contaOTempoDeGravacao() {
    this.pareOCronometro();
    this.cronometro = setInterval(() => {
      this.segundosGravados++;
      if (this.segundosGravados >= LIMITE_DE_GRAVACAO_SEGUNDOS) this.pareGravacao();
    }, 1000);
  }

  get pausado(): boolean {
    return this.gravador.pausado;
  }

  /**
   * ⏸ da barra: pausa DE VERDADE (a gravação continua viva) e monta a prévia
   * do que já foi capturado, para poder ouvir antes de continuar ou enviar.
   */
  pauseAGravacao() {
    if (!this.gravando || this.pausado) return;
    if (!this.gravador.pause()) return;

    this.pareOCronometro();
    this.gravador.espieOParcial().then(previa => {
      // sem prévia (pausou antes do primeiro chunk, ou requestData indisponível)
      // não há tela de pré-envio possível — retomar é a única saída que não
      // deixa a barra num estado pausado sem botão de volta
      if (!previa) {
        this.continueAGravacao();
        return;
      }
      this.descartePendente();
      this.audioPendente = previa;
      this.pontoTocado = -1;
    });
  }

  /** 🎤 com a gravação pausada: continua no MESMO áudio, sem perder o começo. */
  continueAGravacao() {
    if (!this.pausado) return;
    if (!this.gravador.retome()) {
      // recorder não retomou (estado inválido): finalizar ainda salva o que já
      // foi gravado — melhor que uma pausa da qual não se sai
      this.pareGravacao();
      return;
    }

    // só depois de retomar: a prévia era só para ouvir, o arquivo final sai
    // do gravador — e um resume que falhasse não pode deixar a barra sem nada
    this.descartePendente();
    this.contaOTempoDeGravacao();
  }

  /** Encerra a captação e deixa o áudio pronto para ouvir/enviar. */
  pareGravacao(): Promise<void> {
    if (!this.gravando) return Promise.resolve();
    this.pareOCronometro();

    return this.gravador.pare().then(audio => {
      this.gravando = false;
      // painel aberto (veio pela engrenagem): o monitor segue vivo p/ o medidor
      if (!this.microfoneAberto) this.gravador.fecheMonitor();
      this.descartePendente(); // solta a prévia parcial da pausa, se havia
      if (!audio) {
        this.abraMicrofone();
        return;
      }
      this.audioPendente = audio;
      this.pontoTocado = -1;
    });
  }

  canceleGravacao() {
    this.pareOCronometro();
    this.gravador.cancele();
    this.gravador.fecheMonitor();
    this.gravando = false;
    this.zereONivel();
  }

  descartePendente() {
    this.gravador.descarte(this.audioPendente);
    this.audioPendente = null;
    this.pontoTocado = -1;
  }

  /** Lixeira da barra: serve aos dois estados (gravando e pré-envio). */
  descarteAGravacao() {
    if (this.gravando) this.canceleGravacao();
    this.descartePendente();
  }

  /** Microfone da barra de pré-envio: recomeça do zero. */
  regraveOAudio() {
    this.descartePendente();
    this.comeceGravacao();
  }

  /** Avião: gravando, finaliza e manda — como o WhatsApp faz. */
  finalizeEEnvieAudio() {
    if (this.digitando) return;
    if (!this.gravando) {
      this.envieAudio();
      return;
    }
    this.pareGravacao().then(() => this.envieAudio());
  }

  /** Ponto que a reprodução já alcançou (o scrubber pontilhado do WhatsApp). */
  marqueProgressoDaPrevia(segundosTocados: number) {
    const total = this.audioPendente?.duracaoSegundos || 0;
    this.pontoTocado = fatiaAlcancada(fracaoTocada(segundosTocados, total), this.barrasDoMedidor.length);
  }

  /**
   * Marcador da reprodução a 60fps, escrito direto no DOM e FORA do zone.
   *
   * O evento `timeupdate` do <audio> só dispara ~4x por segundo — animar o
   * marcador por ele deixa a bolinha pulando de ponto em ponto. O preenchimento
   * das barras continua no `timeupdate` (degrau ali é o comportamento certo,
   * e é o que o WhatsApp faz); só a bolinha precisa de quadro a quadro.
   */
  sigaAReproducao(player: HTMLAudioElement, marcador: HTMLElement, duracaoMedida: number) {
    this.pareDeSeguirAReproducao(this.donoDaReproducao);
    this.donoDaReproducao = player;

    this.zone.runOutsideAngular(() => {
      const passo = () => {
        if (player.paused || player.ended) {
          this.frameDaReproducao = 0;
          return;
        }
        const total = (isFinite(player.duration) && player.duration > 0)
          ? player.duration
          : duracaoMedida;
        marcador.style.left = (fracaoTocada(player.currentTime, total) * 100).toFixed(2) + '%';
        this.frameDaReproducao = requestAnimationFrame(passo);
      };
      this.frameDaReproducao = requestAnimationFrame(passo);
    });
  }

  /**
   * Pausou/terminou: para o loop — mas só se o player que pausou for o DONO
   * dele. Vários <audio> podem tocar ao mesmo tempo (bolhas não se pausam
   * entre si), e o pause de um não pode congelar o marcador de outro.
   */
  pareDeSeguirAReproducao(player?: HTMLAudioElement | null) {
    if (player && player !== this.donoDaReproducao) return;
    if (this.frameDaReproducao) cancelAnimationFrame(this.frameDaReproducao);
    this.frameDaReproducao = 0;
    this.donoDaReproducao = null;
  }

  /** Fim do áudio: marcador volta ao começo junto com a onda. */
  zereOMarcador(marcador: HTMLElement, player?: HTMLAudioElement) {
    this.pareDeSeguirAReproducao(player);
    marcador.style.left = '0%';
  }

  /**
   * Preenche a onda da bolha conforme a reprodução anda, como no WhatsApp.
   * A duração do elemento é preferida quando existe: webm do MediaRecorder
   * costuma vir com `duration` infinita, e aí vale a que medimos no relógio.
   */
  marqueOndaDaBolha(mensagem: MensagemSimulador, segundosTocados: number, duracaoDoElemento: number) {
    const audio = mensagem.audio;
    if (!audio || !audio.onda.length) return;

    const total = (isFinite(duracaoDoElemento) && duracaoDoElemento > 0)
      ? duracaoDoElemento
      : audio.duracaoSegundos;
    audio.tocado = fatiaAlcancada(fracaoTocada(segundosTocados, total), audio.onda.length);
  }

  /** Blob gravado → base64 puro (sem o prefixo data:), que é o que o STT recebe. */
  private leiaAudioComoBase64(blob: Blob): Promise<string> {
    return new Promise((resolva, rejeite) => {
      const leitor = new FileReader();
      leitor.onerror = () => rejeite(leitor.error);
      leitor.onload = () => resolva(String(leitor.result || '').split(',')[1] || '');
      leitor.readAsDataURL(blob);
    });
  }

  /**
   * Manda o áudio, como no WhatsApp: o binário sobe e o Servidor decide pelo
   * comportamento da loja. Com "entender e responder" a fala é transcrita e a
   * resposta vem do turno (ou da máquina do checkout, quando é a vez dela).
   */
  envieAudio() {
    const audio = this.audioPendente;
    if (!audio || this.digitando || this.emAtendimentoHumano) return;
    if (!this.servico.envieAudioSimulador) return; // widget não tem microfone

    this.audioPendente = null;
    const bolha: MensagemSimulador = {
      papel: 'cliente',
      texto: '',
      audio: {
        url: audio.url,
        urlSegura: audio.urlSegura,
        duracaoSegundos: audio.duracaoSegundos,
        onda: this.alturasDaOnda(audio.picos),
        tocado: -1,
      },
      horario: new Date(),
    };
    this.mensagens.push(bolha);
    this.deveRolarParaOFim = true;
    this.digitando = true;

    this.leiaAudioComoBase64(audio.blob)
      .then(base64 => this.servico.envieAudioSimulador!(this.telefoneSimulado,
        { base64, mimeType: audio.mimeType }))
      .then((resposta: any) => {
        // a fala foi transcrita: a bolha mostra o que a Mia entendeu e a resposta
        // segue pelo mesmo renderizador de quem digitou
        if (resposta?.transcricaoDeAudio) {
          if (bolha.audio) bolha.audio.transcricao = resposta.transcricaoDeAudio;
          if (resposta.origem === 'checkout') this.renderizeAcoesCheckout(resposta);
          else this.renderizeRespostaDoTurno(resposta);
          return;
        }

        for (const acao of resposta?.acoes || []) {
          if (acao.tipo === 'enviarTexto' && acao.texto) {
            this.mensagens.push({ papel: 'mia', texto: acao.texto, horario: new Date() });
          }
        }

        if (resposta?.escalou) {
          this.entreEmAtendimentoHumano();
        } else if (resposta?.emAtendimentoHumano) {
          this.emAtendimentoHumano = true;
          this.anote('🙋 A conversa já está com um atendente — a Mia não responde até devolverem.');
        } else if (resposta?.silencio) {
          this.anote(this.explicacaoDoSilencio(resposta.motivo));
        }
      })
      .catch(() => {
        this.avisoTecnico = true;
        this.mensagens.push({
          papel: 'mia',
          texto: this.textoErroTecnico('⚠️ Não consegui processar o áudio — verifique o Servidor.'),
          erroTecnico: true,
          horario: new Date(),
        });
      })
      .then(() => {
        this.digitando = false;
        this.deveRolarParaOFim = true;
      });
  }

  /** A Mia calou: sem explicação isso parece defeito do simulador. */
  private explicacaoDoSilencio(motivo: string): string {
    if (motivo === 'comportamento_nao_responder') {
      return '🔇 A Mia não respondeu — a loja está configurada como "não responder" a áudios. O áudio ficou registrado na conversa.';
    }
    if (motivo === 'rajada_apos_aviso') {
      return '🔇 A Mia não respondeu — áudio recebido logo depois do aviso (ela considera que foi gravado antes de o cliente ler).';
    }
    return '🔇 A Mia não respondeu — o cliente insistiu no áudio, mas esta loja não escala para atendente.';
  }

  private entreEmAtendimentoHumano() {
    this.emAtendimentoHumano = true;
    this.anote('🙋 Conversa transferida para atendimento humano — como no WhatsApp, a Mia para por aqui.');
  }

  devolvaParaMia() {
    if (this.devolvendoParaMia || !this.servico.devolvaSimuladorParaMia) return;
    this.devolvendoParaMia = true;

    this.servico.devolvaSimuladorParaMia(this.telefoneSimulado)
      .then(() => {
        this.emAtendimentoHumano = false;
        this.anote('↩️ Conversa devolvida para a Mia — pode continuar o teste.');
      })
      .catch(() => {
        this.avisoTecnico = true;
        this.anote('⚠️ Não consegui devolver a conversa — verifique o Servidor.');
      })
      .then(() => {
        this.devolvendoParaMia = false;
        this.deveRolarParaOFim = true;
      });
  }

  private anote(texto: string) {
    this.mensagens.push({ papel: 'mia', texto: '', notaSistema: texto, horario: new Date() });
    this.deveRolarParaOFim = true;
  }

  // ==== painel do microfone (dispositivo, nível ao vivo e teste) ====

  abraMicrofone() {
    this.microfoneAberto = true;
    this.gravador.abraMonitor().then(() => this.gravador.marqueComoConferido());
  }

  fecheMicrofone() {
    this.microfoneAberto = false;
    this.pareTesteDeMicrofone();
    this.gravador.descarte(this.audioDeTeste);
    this.audioDeTeste = null;
    // gravando, o monitor continua vivo — quem fecha o painel não perde a captação
    if (!this.gravando) {
      this.gravador.fecheMonitor();
      this.zereONivel();
    }
  }

  troqueMicrofone(deviceId: string) {
    this.gravador.escolhaMicrofone(deviceId);
  }

  /** "Começar a gravar" do painel: fecha o painel sem soltar o microfone. */
  graveAgoraPeloPainel() {
    this.microfoneAberto = false;
    this.gravador.descarte(this.audioDeTeste);
    this.audioDeTeste = null;
    this.comeceGravacao();
  }

  /** Grava um trecho curto e devolve para ouvir — o "teste" do microfone. */
  graveTesteDeMicrofone() {
    if (this.gravandoTeste) {
      this.pareTesteDeMicrofone();
      return;
    }

    // gravação de mensagem em curso: o teste dividiria o MESMO recorder e o
    // pare() do teste entregaria a mensagem do usuário como áudio de teste
    if (this.gravando) return;

    this.gravador.descarte(this.audioDeTeste);
    this.audioDeTeste = null;

    this.gravador.comece().then(comecou => {
      if (!comecou) return;
      this.gravandoTeste = true;
      this.segundosGravados = 0;
      this.pareOCronometro();
      this.cronometro = setInterval(() => {
        this.segundosGravados++;
        if (this.segundosGravados >= 5) this.pareTesteDeMicrofone();
      }, 1000);
    });
  }

  pareTesteDeMicrofone() {
    if (!this.gravandoTeste) return;
    this.pareOCronometro();
    this.gravador.pare().then(audio => {
      this.gravandoTeste = false;
      this.audioDeTeste = audio;
    });
  }

  /** Botão do aviso, no espírito do Meet: cada problema tem a próxima ação. */
  acaoDoProblema() {
    const problema = this.gravador.problema;
    if (!problema) return;

    if (problema.acao === 'tentar_de_novo') {
      this.gravador.limpeProblema();
      this.gravador.abraMonitor();
      return;
    }

    if (problema.acao === 'escolher_microfone') {
      this.gravador.limpeProblema();
      this.abraMicrofone();
    }
  }

  // ==== medidor de nível ====

  /**
   * Chamado ~60x/s FORA do zone do Angular: escreve as barras direto no DOM.
   * Passar por binding aqui dispararia change detection a cada frame e travaria
   * o painel inteiro.
   */
  private desenheNivel(nivel: number) {
    this.historicoDeNivel.push(nivel);
    this.historicoDeNivel.shift();
    this.pinteBarras(this.medidorGravacao);
    this.pinteBarras(this.medidorPainel);
  }

  private pinteBarras(alvo: ElementRef) {
    const elemento = alvo && alvo.nativeElement;
    if (!elemento) return;

    const pontos = elemento.children;
    const inicio = this.historicoDeNivel.length - pontos.length;
    for (let i = 0; i < pontos.length; i++) {
      const valor = Math.min(1, this.historicoDeNivel[inicio + i] || 0);
      // pontilhado discreto como o do WhatsApp: o nível engorda e acende o ponto
      pontos[i].style.transform = `scale(${(1 + valor * 1.9).toFixed(2)})`;
      pontos[i].style.opacity = String(0.28 + valor * 0.72);
    }
  }

  private zereONivel() {
    this.historicoDeNivel = new Array(this.historicoDeNivel.length).fill(0);
    this.pinteBarras(this.medidorGravacao);
    this.pinteBarras(this.medidorPainel);
  }

  private pareOCronometro() {
    if (this.cronometro) clearInterval(this.cronometro);
    this.cronometro = null;
  }

  duracaoEmMinutos(segundos: number): string {
    const minutos = Math.floor(segundos / 60);
    const resto = segundos % 60;
    return `${minutos}:${resto < 10 ? '0' : ''}${resto}`;
  }

  envie() {
    const texto = (this.textoDigitado || '').trim();
    const imagem = this.imagemPendente;
    if ((!texto && !imagem) || this.digitando || this.emAtendimentoHumano) return;

    this.mensagens.push({ papel: 'cliente', texto, imagem: imagem?.preview, horario: new Date() });
    this.textoDigitado = '';
    this.imagemPendente = null;
    this.deveRolarParaOFim = true;

    // checkout aguardando rua/número/bairro: o texto vai à máquina, não ao LLM
    // (se a máquina não conseguir interpretar, ela devolve o texto pra Mia).
    // Imagem nunca é resposta da máquina — vai ao LLM, como no WhatsApp real.
    if (!imagem && this.aguardandoTextoCheckout) {
      this.envieEventoCheckout({ tipo: 'texto', texto }, texto);
      return;
    }

    this.digitando = true;
    this.envieTurnoLLM(
      texto,
      imagem ? { base64: imagem.base64, mimeType: imagem.mimeType, legenda: texto || undefined } : undefined
    );
  }

  /** A reação da Mia cai na última fala do cliente (é a mensagem que ela respondeu). */
  private reajaAUltimaMensagemDoCliente(emoji: string) {
    for (let i = this.mensagens.length - 1; i >= 0; i--) {
      const mensagem = this.mensagens[i];
      if (mensagem.papel === 'cliente' && !mensagem.notaSistema) {
        mensagem.reacao = emoji;
        return;
      }
    }
  }

  /**
   * Desenha a resposta de um turno da Mia — vale para o texto digitado e para a
   * fala transcrita do microfone, que chega pelo mesmo caminho no Servidor.
   */
  private renderizeRespostaDoTurno(resposta: any) {
    // um atendente assumiu (pelo áudio ou pela tela de conversas): a Mia
    // fica em silêncio aqui também, como no WhatsApp
    if (resposta?.emAtendimentoHumano) {
      this.emAtendimentoHumano = true;
      this.anote('🙋 A conversa está com um atendente — a Mia não responde até devolverem.');
      return;
    }
    if (resposta?.carrinho) {
      this.carrinho = resposta.carrinho;
    }
    const ferramentasDoTurno = resposta?.ferramentas?.length ? resposta.ferramentas : undefined;
    const indiceInicial = this.mensagens.length;
    const acoes = resposta?.acoes || [];
    for (const acao of acoes) {
      if (acao.tipo === 'enviarTexto' && acao.texto) {
        const erroTecnico = acao.texto.startsWith(TEXTO_FALLBACK_PREFIXO);
        if (erroTecnico) this.avisoTecnico = true;
        this.mensagens.push({
          papel: 'mia',
          texto: acao.texto,
          horario: new Date(),
          telemetria: resposta?.telemetria,
          erroTecnico
        });
      } else if (acao.tipo === 'enviarImagem' && acao.url) {
        // como no WhatsApp: imagem com a legenda na mesma bolha
        this.mensagens.push({
          papel: 'mia',
          texto: acao.legenda || '',
          imagem: acao.url,
          horario: new Date(),
          telemetria: resposta?.telemetria
        });
      } else if (acao.tipo === 'enviarBotoes' && acao.botoes?.length) {
        this.mensagens.push({
          papel: 'mia',
          texto: acao.texto || '',
          // ações da Mia (LLM) trazem botões como string; normaliza p/ {titulo}
          botoes: acao.botoes.map((botao: any) =>
            typeof botao === 'string' ? { titulo: botao } : botao),
          imagem: acao.imagem,
          horario: new Date(),
          telemetria: resposta?.telemetria
        });
      } else if (acao.tipo === 'enviarCta' && acao.link) {
        // link da página de montagem: guarda o token para aceitar a bolha que a página devolve ao concluir
        const tokenMontagem = String(acao.link).match(/\/montar\/([A-Za-z0-9_-]+)\/?$/);
        if (tokenMontagem) this.tokensDeMontagem.add(tokenMontagem[1]);
        // cta_url: foto (header) + texto + rodapé + botão de link numa mensagem só
        this.mensagens.push({
          papel: 'mia',
          texto: acao.texto || '',
          imagem: acao.imagem,
          linkCta: acao.link,
          ctaBotao: resposta?.cta?.textoBotao,
          ctaRodape: resposta?.cta?.textoFooter,
          horario: new Date(),
          telemetria: resposta?.telemetria
        });
      } else if (acao.tipo === 'enviarLista' && acao.itens?.length) {
        this.mensagens.push({
          papel: 'mia',
          texto: acao.texto || '',
          lista: { rotulo: acao.rotuloBotao || 'Ver opções', itens: acao.itens },
          vitrine: acao.vitrine,
          maisCategorias: acao.maisCategorias,
          horario: new Date(),
          telemetria: resposta?.telemetria
        });
      } else if (acao.tipo === 'reagir' && acao.emoji) {
        // como no WhatsApp real: o emoji vai na mensagem do cliente, não numa bolha nova
        this.reajaAUltimaMensagemDoCliente(acao.emoji);
      } else if (acao.tipo === 'abrirFlowCheckout') {
        // a máquina de estados assume o fechamento na conversa (botões, $0)
        this.inicieCheckoutConversacional();
      } else if (acao.tipo === 'abrirVitrineCategoria' && acao.vitrine?.produtos?.length) {
        // cartão-Flow de categoria: foto de destaque + resumo + botão da vitrine
        this.mensagens.push({
          papel: 'mia',
          texto: acao.texto || '',
          imagem: acao.imagem,
          flowVitrine: true,
          rotuloFlow: acao.rotuloBotao || 'Ver Produtos',
          vitrine: acao.vitrine,
          horario: new Date(),
          telemetria: resposta?.telemetria
        });
      }
    }

    // chips das ferramentas usadas neste turno (na primeira bolha da resposta)
    if (ferramentasDoTurno && this.mensagens.length > indiceInicial) {
      this.mensagens[indiceInicial].ferramentas = ferramentasDoTurno;
    }

    // checador de fatos: preço citado sem origem verificável
    const precosSuspeitos = resposta?.telemetria?.precosSuspeitos;
    if (precosSuspeitos?.length && this.mensagens.length > indiceInicial) {
      this.mensagens[indiceInicial].avisoPreco =
        precosSuspeitos.map((valor: number) => 'R$ ' + valor.toFixed(2).replace('.', ',')).join(', ');
    }
  }

  private envieTurnoLLM(texto: string, imagem?: ImagemDoChat) {
    this.servico.envieTurnoSimulador(this.telefoneSimulado, texto, this.pedidoAbertoTeste, imagem, this.perfilSimuladoParaEnvio)
      .then((resposta: any) => {
        this.renderizeRespostaDoTurno(resposta);
      })
      .catch(() => {
        this.avisoTecnico = true;
        this.mensagens.push({
          papel: 'mia',
          texto: this.textoErroTecnico('⚠️ Não consegui falar com a Mia V3. Verifique se o serviço está rodando na porta 4330.'),
          horario: new Date(),
          erroTecnico: true
        });
      })
      .then(() => {
        this.digitando = false;
        this.deveRolarParaOFim = true;
        setTimeout(() => this.campoMensagem?.nativeElement?.focus(), 0);
      });
  }

  aoTeclar(evento: KeyboardEvent) {
    if (evento.key === 'Enter' && !evento.shiftKey) {
      evento.preventDefault();
      this.envie();
    }
  }

  // ==== link → botão CTA (espelha o cta_url da Cloud API) ====

  linkDaMensagem(mensagem: MensagemSimulador): string | null {
    if (mensagem.papel !== 'mia' || mensagem.erroTecnico) return null;
    // cta_url é um tipo interativo EXCLUSIVO na Cloud API: mensagem com lista,
    // botões ou Flow não pode ter botão de link — o link fica como texto no corpo
    if (mensagem.lista || mensagem.botoes?.length || mensagem.flowCheckout ||
        mensagem.flowVitrine || mensagem.pedirLocalizacao) return null;
    if (mensagem.linkCta) return mensagem.linkCta;
    const match = (mensagem.texto || '').match(/https?:\/\/[^\s]+/);
    return match ? match[0] : null;
  }

  textoSemLink(mensagem: MensagemSimulador): string {
    const link = this.linkDaMensagem(mensagem);
    if (!link) return mensagem.texto;
    return mensagem.texto
      .replace(link, '')
      .replace(/[ \t]*(👉|👇|:)?[ \t]*\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  rotuloDoLink(mensagem: MensagemSimulador, link: string): string {
    // bolha vinda de enviarCta ([BOTAO_CARDAPIO]): rótulo resolvido no servidor
    // com a config da loja — link solto no texto fica no contextual, como no real
    if (mensagem.ctaBotao) return mensagem.ctaBotao;
    if (/meucardapio|meucatalogo/i.test(link)) return 'Fazer Pedido';
    return 'Acessar';
  }

  descricaoTelemetria(telemetria: any): string {
    if (!telemetria) return '';
    const partes = [telemetria.modelo];
    partes.push(`${telemetria.tokensEntrada || 0}→${telemetria.tokensSaida || 0} tokens`);
    if (telemetria.tokensCacheEscrita) partes.push(`cache escrita ${telemetria.tokensCacheEscrita}`);
    if (telemetria.tokensCache) partes.push(`cache ${telemetria.tokensCache}`);
    if (telemetria.custoUsd != null) partes.push(`$${Number(telemetria.custoUsd).toFixed(5)}`);
    if (telemetria.latenciaMs != null) partes.push(`${(telemetria.latenciaMs / 1000).toFixed(1)}s`);
    return partes.join(' · ');
  }
}
